import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

// Global styles for better mobile experience
const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: #333;
    background-color: #fff;
  }
`;

// Styled components for better maintainability
const Container = styled.div`
  min-height: 100vh;
  padding: 2rem 1rem;
  background-color: #ffffff;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.header`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #f0f0f0;

  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  color: #000;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    color: #555;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.5rem;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #555;
  font-weight: 300;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }
`;

const ProjectHero = styled.div`
  width: 100%;
  height: 250px;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  position: relative;

  @media (min-width: 768px) {
    height: 400px;
    margin-bottom: 2rem;
  }
`;

const ProjectContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

const MainContent = styled.div`
  flex: 1;
  order: 2;

  @media (min-width: 992px) {
    order: 1;
  }
`;

const SideContent = styled.div`
  order: 1;

  @media (min-width: 992px) {
    width: 300px;
    order: 2;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #000;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;

  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Description = styled.p`
  margin-bottom: 1.5rem;
  color: #555;
  line-height: 1.8;
  font-size: 0.95rem;

  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const FeaturesList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  list-style-type: disc;
`;

const FeatureItem = styled.li`
  margin-bottom: 0.5rem;
  color: #555;
  font-size: 0.95rem;

  @media (min-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const TechContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TechPill = styled.span`
  background-color: #f0f0f0;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #333;
`;

const ProjectLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (min-width: 480px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const LinkButton = styled.a`
  padding: 0.7rem 1.2rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  text-align: center;
  display: inline-block;

  &.demo {
    background-color: #000;
    color: #fff;

    &:hover {
      background-color: #333;
    }
  }

  &.code {
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;

    &:hover {
      background-color: #eee;
    }
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const DetailsCard = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 1.2rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  margin-bottom: 0.8rem;
  display: flex;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;

  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #777;
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: #333;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (min-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    margin-bottom: 2rem;
  }
`;

const GalleryImage = styled.div`
  width: 100%;
  height: 150px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
  }

  @media (min-width: 480px) {
    height: 180px;
  }

  @media (min-width: 768px) {
    height: 200px;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.8rem;
`;

const TagPill = styled.span`
  background-color: #e0e0e0;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  color: #555;
`;

const Loading = styled.div`
  text-align: center;
  font-size: 1.2rem;
  padding: 2rem;
`;

const Error = styled.div`
  text-align: center;
  color: #e74c3c;
  padding: 2rem;
  font-size: 1.1rem;
`;

const ProjectDetails = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const response = await fetch(`https://my-portfoli-backed.onrender.com/api/projects/${projectId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setProject(data.data.project);
      } catch (err) {
        console.error('Error details:', err);
        setError('حدث خطأ في جلب بيانات المشروع. يرجى المحاولة لاحقاً');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG');
  };

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <Loading>جاري تحميل تفاصيل المشروع...</Loading>
      </>
    );
  }

  if (error) {
    return (
      <>
        <GlobalStyle />
        <Error>حدث خطأ: {error}</Error>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <GlobalStyle />
        <Error>المشروع غير موجود</Error>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Container>
        <Content>
          <Header>
            <BackButton to="/projects">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              العودة إلى المشاريع
            </BackButton>
            <Title>{project.details?.title || 'لا يوجد عنوان'}</Title>
            {project.meta?.subtitle && <Subtitle>{project.meta.subtitle}</Subtitle>}
            {project.meta?.tags && (
              <TagContainer>
                {project.meta.tags.map((tag, index) => (
                  <TagPill key={index}>{tag}</TagPill>
                ))}
              </TagContainer>
            )}
          </Header>

          <ProjectHero
            style={{
              backgroundImage: project.media?.coverImage 
                ? `url(${project.media.coverImage})` 
                : 'none'
            }}
          >
            {!project.media?.coverImage && (
              <span style={{ color: '#999', fontSize: '1rem' }}>[صورة المشروع الرئيسية]</span>
            )}
          </ProjectHero>

          <ProjectContent>
            <MainContent>
              <section>
                <SectionTitle>عن المشروع</SectionTitle>
                <Description>
                  {project.details?.description || 'لا يوجد وصف متوفر للمشروع'}
                </Description>
              </section>

              {project.details?.features && project.details.features.length > 0 && (
                <section>
                  <SectionTitle>المميزات الرئيسية</SectionTitle>
                  <FeaturesList>
                    {project.details.features.map((feature, index) => (
                      <FeatureItem key={index}>{feature}</FeatureItem>
                    ))}
                  </FeaturesList>
                </section>
              )}

              {project.media?.gallery && project.media.gallery.length > 0 && (
                <section>
                  <SectionTitle>معرض الصور</SectionTitle>
                  <Gallery>
                    {project.media.gallery.map((image, index) => (
                      <GalleryImage 
                        key={index} 
                        style={{ backgroundImage: `url(${image})` }}
                        onClick={() => setSelectedImage(image)}
                      />
                    ))}
                  </Gallery>
                </section>
              )}
            </MainContent>

            <SideContent>
              <DetailsCard>
                <SectionTitle>تفاصيل المشروع</SectionTitle>
                
                <DetailItem>
                  <DetailLabel>الحالة:</DetailLabel>
                  <DetailValue>{project.meta?.status || 'غير محدد'}</DetailValue>
                </DetailItem>
                
                <DetailItem>
                  <DetailLabel>تاريخ البدء:</DetailLabel>
                  <DetailValue>{formatDate(project.meta?.startDate)}</DetailValue>
                </DetailItem>
                
                {project.details?.endDate && (
                  <DetailItem>
                    <DetailLabel>تاريخ الانتهاء:</DetailLabel>
                    <DetailValue>{formatDate(project.details.endDate)}</DetailValue>
                  </DetailItem>
                )}
                
                <DetailItem>
                  <DetailLabel>مميز:</DetailLabel>
                  <DetailValue>
                    {project.meta?.isFeatured ? 'نعم' : 'لا'}
                  </DetailValue>
                </DetailItem>
              </DetailsCard>

              {project.technical?.technologies && project.technical.technologies.length > 0 && (
                <section>
                  <SectionTitle>التقنيات المستخدمة</SectionTitle>
                  <TechContainer>
                    {project.technical.technologies.map((tech, index) => (
                      <TechPill key={index}>{tech}</TechPill>
                    ))}
                  </TechContainer>
                </section>
              )}

              <section>
                <SectionTitle>الروابط</SectionTitle>
              <ProjectLinks>
  {project.technical?.liveUrl ? (
    <LinkButton 
      href={project.technical.liveUrl} 
      className="demo"
      target="_blank"
      rel="noopener noreferrer"
    >
      عرض المشروع
    </LinkButton>
  ) : (
    <LinkButton 
      as="span" // يحول الزر إلى span غير قابل للنقر
      className="demo"
      style={{
        cursor: 'not-allowed',
        opacity: 0.7
      }}
    >
      عرض المشروع
    </LinkButton>
  )}

  {project.technical?.repositoryUrl ? (
    <LinkButton 
      href={project.technical.repositoryUrl} 
      className="code"
      target="_blank"
      rel="noopener noreferrer"
    >
      الكود المصدر
    </LinkButton>
  ) : (
    <LinkButton 
      as="span"
      className="code"
      style={{
        cursor: 'not-allowed',
        opacity: 0.7
      }}
    >
      الكود المصدر
    </LinkButton>
  )}
</ProjectLinks>
              </section>
            </SideContent>
          </ProjectContent>
        </Content>
      </Container>

      {/* Image Modal - would need to implement this */}
      {selectedImage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={() => setSelectedImage(null)}>
          <img 
            src={selectedImage} 
            alt="Preview" 
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: '8px'
            }} 
          />
        </div>
      )}
    </>
  );
};

export default ProjectDetails;